import { SplashScreen } from '@capacitor/splash-screen';

import { logger } from 'utils/logger';
import { getIsNativePlatform } from 'utils/platform';

import {
  SPLASH_FADE_OUT_MS,
  SPLASH_FAILSAFE_MS,
  WEB_SPLASH_ELEMENT_ID,
} from './nativeSplash.constants';

const failsafeState = { armed: false };

// Suppress the WebView boot splash (#splash-screen in public/index.html) on native so it never
// flashes between the WebView loading index.html and React mounting. The HELD native splash
// (capacitor.config SplashScreen.launchAutoHide:false) covers that whole window, so a second web
// splash underneath it would only show as a brief mismatched flicker. Web (non-native) is
// untouched — the web splash keeps doing its job there.
export const suppressWebSplashOnNative = () => {
  if (!getIsNativePlatform()) {
    return;
  }

  const webSplash = document.getElementById(WEB_SPLASH_ELEMENT_ID);

  if (webSplash) {
    webSplash.style.display = 'none';
  }
};

// Hide the held native splash once React has rendered. Called AFTER createRoot().render() so the
// native splash covers the entire boot (incl. the WebView load + first paint) and then fades to
// the live app — a single brand splash, no double logo. No-op on web (there is no native splash).
export const hideNativeSplash = async () => {
  if (!getIsNativePlatform()) {
    return;
  }

  try {
    await SplashScreen.hide({ fadeOutDuration: SPLASH_FADE_OUT_MS });
  } catch (error) {
    logger.error('[NativeSplash] hide failed', error);
  }
};

// One-shot guard against a wedged boot. The native splash is HELD until hideNativeSplash() runs in
// the boot chain (src/index.js). If that chain throws or never settles — e.g. the biometric-resume
// path where the rAF-scheduled hide is starved while the splash occludes the WebView — the splash
// would stay up forever. Arm a timer at boot that force-hides after a ceiling, independent of React
// and rAF. No-op on web; idempotent (a later hideNativeSplash() is harmless once already hidden).
export const armNativeSplashFailsafe = (timeoutMs = SPLASH_FAILSAFE_MS) => {
  if (!getIsNativePlatform() || failsafeState.armed) {
    return;
  }

  failsafeState.armed = true;
  setTimeout(() => {
    hideNativeSplash();
  }, timeoutMs);
};
